import { auth, db } from "@/lib/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const seedSchema = z.object({
  dealId: z.string().min(1, "Deal ID is required"),
  amount: z.coerce.number().min(1, "Amount must be greater than 0"),
  rate: z.coerce.number().min(0).max(100, "Rate must be between 0 and 100"),
  amortYears: z.coerce.number().min(1).max(30, "Amortization years must be between 1 and 30"),
  conditions: z.string().min(1, "At least one condition is required"),
});

type SeedFormData = z.infer<typeof seedSchema>;

export default function Seed() {
  const [uid, setUid] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const form = useForm<SeedFormData>({
    resolver: zodResolver(seedSchema),
    defaultValues: {
      dealId: "deal-ABC123",
      amount: 1200000,
      rate: 8.25,
      amortYears: 7,
      conditions: "DSCR ≥ 1.25\nPersonal guarantee 50%",
    },
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        try {
          await signInAnonymously(auth);
        } catch (error) {
          console.error("Error signing in anonymously:", error);
          toast.error("Authentication failed");
        }
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  async function seedDefaultData() {
    if (!uid) {
      toast.error("No UID available. Please wait a moment and try again.");
      return;
    }

    setIsSeeding(true);
    try {
      const baseCollection = collection(db, `lenders/${uid}/submissions`);
      const defaultRows = [
        {
          dealId: "deal-ABC123",
          amount: 1200000,
          rate: 8.25,
          amortYears: 7,
          conditions: ["DSCR ≥ 1.25", "Personal guarantee 50%"],
          status: "draft",
          createdAt: serverTimestamp(),
        },
        {
          dealId: "deal-XYZ999",
          amount: 850000,
          rate: 9.1,
          amortYears: 6,
          conditions: ["Second lien mortgage", "NDA signed"],
          status: "draft",
          createdAt: serverTimestamp(),
        },
        {
          dealId: "deal-HELLO42",
          amount: 2000000,
          rate: 7.9,
          amortYears: 10,
          conditions: ["Quarterly covenant"],
          status: "draft",
          createdAt: serverTimestamp(),
        },
      ];

      for (const row of defaultRows) {
        await addDoc(baseCollection, row);
      }

      toast.success("Successfully created 3 test submissions!");
    } catch (error) {
      console.error("Error seeding data:", error);
      toast.error("Failed to seed data. Check console for details.");
    } finally {
      setIsSeeding(false);
    }
  }

  async function onSubmit(data: SeedFormData) {
    if (!uid) {
      toast.error("No UID available. Please wait a moment and try again.");
      return;
    }

    setIsSeeding(true);
    try {
      const baseCollection = collection(db, `lenders/${uid}/submissions`);
      const conditionsArray = data.conditions
        .split("\n")
        .map((c) => c.trim())
        .filter(Boolean);

      await addDoc(baseCollection, {
        dealId: data.dealId,
        amount: data.amount,
        rate: data.rate,
        amortYears: data.amortYears,
        conditions: conditionsArray,
        status: "draft",
        createdAt: serverTimestamp(),
      });

      toast.success("Successfully created custom submission!");
      form.reset();
    } catch (error) {
      console.error("Error creating submission:", error);
      toast.error("Failed to create submission. Check console for details.");
    } finally {
      setIsSeeding(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Development Seed Tool</h1>
        <p className="text-muted-foreground">
          Create test data for lender submissions
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current User Information</CardTitle>
          <CardDescription>
            Authenticated user ID for seeding data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">UID:</span>
            <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
              {uid ?? "Loading..."}
            </code>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Seed</CardTitle>
          <CardDescription>
            Create 3 default test submissions instantly
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={seedDefaultData}
            disabled={isSeeding || !uid}
            className="w-full sm:w-auto"
          >
            {isSeeding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating submissions...
              </>
            ) : (
              "Create 3 Test Submissions"
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom Submission</CardTitle>
          <CardDescription>
            Create a custom test submission with specific values
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="dealId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal ID</FormLabel>
                    <FormControl>
                      <Input placeholder="deal-ABC123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Amount ($)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="1200000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rate (%)</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="8.25" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="amortYears"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Amortization (Years)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="7" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="conditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Conditions (one per line)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="DSCR ≥ 1.25&#10;Personal guarantee 50%"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isSeeding || !uid} className="w-full sm:w-auto">
                {isSeeding ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Custom Submission"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
